import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  approveSlotRequest,
  getBusinessSlotRequests,
  getSlotRequestPendingCount,
  rejectSlotRequest,
  type SlotRequestRow,
} from "@/api/endpoints/slot-requests"

export const slotRequestKeys = {
  all: ["slot-requests"] as const,
  business: (businessId: string, status?: string) =>
    [...slotRequestKeys.all, "business", businessId, status ?? "all"] as const,
  pendingCount: (businessId: string) =>
    [...slotRequestKeys.all, "pending-count", businessId] as const,
}

export function useGetBusinessSlotRequestsQuery(params: {
  businessId?: string
  status?: string
  enabled?: boolean
}) {
  const { businessId, status, enabled = true } = params
  return useQuery({
    queryKey: slotRequestKeys.business(businessId ?? "", status),
    queryFn: () =>
      getBusinessSlotRequests({
        business: businessId!,
        status,
      }),
    enabled: enabled && !!businessId,
    select: (res) => res.data as SlotRequestRow[],
  })
}

export function useGetSlotRequestPendingCountQuery(businessId?: string) {
  return useQuery({
    queryKey: slotRequestKeys.pendingCount(businessId ?? ""),
    queryFn: () => getSlotRequestPendingCount(businessId!),
    enabled: !!businessId,
    select: (res) => res.data.count,
    refetchInterval: 60_000,
  })
}

type SlotRequestListCache = {
  data: SlotRequestRow[]
  message?: string
  error?: string | number
}

function isSlotRequestListCache(value: unknown): value is SlotRequestListCache {
  return (
    !!value &&
    typeof value === "object" &&
    Array.isArray((value as SlotRequestListCache).data)
  )
}

/** queryFn caches the full API envelope; `select` only reshapes for observers. */
function patchSlotRequestLists(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  updater: (row: SlotRequestRow) => SlotRequestRow
) {
  queryClient.setQueriesData<unknown>(
    { queryKey: [...slotRequestKeys.all, "business"] },
    (old: unknown) => {
      if (!isSlotRequestListCache(old)) return old
      return {
        ...old,
        data: old.data.map((row) => (row._id === id ? updater(row) : row)),
      }
    }
  )
}

export function useApproveSlotRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      approvalType,
    }: {
      id: string
      approvalType: "ONE_OFF" | "RECURRING"
    }) => approveSlotRequest(id, approvalType),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: slotRequestKeys.all })
      const previous = queryClient.getQueriesData<unknown>({
        queryKey: [...slotRequestKeys.all, "business"],
      })
      patchSlotRequestLists(queryClient, id, (row) => ({
        ...row,
        status: "APPROVED",
      }))
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: slotRequestKeys.all })
    },
  })
}

export function useRejectSlotRequestMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      rejectSlotRequest(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: slotRequestKeys.all })
      const previous = queryClient.getQueriesData<unknown>({
        queryKey: [...slotRequestKeys.all, "business"],
      })
      patchSlotRequestLists(queryClient, id, (row) => ({
        ...row,
        status: "REJECTED",
      }))
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: slotRequestKeys.all })
    },
  })
}
